import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { LINE_STATES, numRef } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { PackageCheck, Truck, MapPin, Clock, Hand } from "lucide-react";
import { toast } from "sonner";

function ItemRow({ it, markState, navigate }) {
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`log-item-${it.linha_id}`}>
      <td className="px-3 py-2">
        <button className="text-sky-700 font-semibold hover:underline" onClick={() => navigate(`/pedido/${it.atendimento_id}`)}>{numRef(it.atendimento_numero, it.atendimento_codigo)}</button>
      </td>
      <td className="px-3 py-2 font-medium text-slate-800">{it.descricao}</td>
      <td className="px-3 py-2 font-mono text-slate-600">{it.ref_marca || "—"}</td>
      <td className="px-3 py-2 text-center">{it.qtd}</td>
      <td className="px-3 py-2 text-slate-600">{it.cliente || "—"}</td>
      <td className="px-3 py-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${LINE_STATES[it.estado]?.badge}`}>{LINE_STATES[it.estado]?.label || it.estado}</span>
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {it.estado !== "em_transito" && (
          <Button size="sm" variant="outline" className="h-7 text-xs mr-1" onClick={() => markState(it, "em_transito")} data-testid={`log-transito-${it.linha_id}`}>Em trânsito</Button>
        )}
        <Button size="sm" className="h-7 text-xs bg-teal-600 hover:bg-teal-700" onClick={() => markState(it, "recebido")} data-testid={`log-receber-${it.linha_id}`}>Recebido</Button>
      </td>
    </tr>
  );
}

function GroupCard({ grp, recolha, markState, markAll, navigate }) {
  const o = grp.origem;
  return (
    <div className={`bg-white rounded-lg border shadow-sm overflow-hidden ${recolha ? "border-orange-200" : "border-slate-200"}`} data-testid={`log-grupo-${o?.id || grp.fornecedor?.id || "sem"}`}>
      <div className={`px-4 py-2.5 flex items-center justify-between ${recolha ? "bg-orange-50 border-b border-orange-100" : "bg-slate-50 border-b border-slate-100"}`}>
        <div className="flex items-center gap-2">
          {recolha ? <Hand size={15} className="text-orange-600" /> : <Truck size={15} className="text-slate-500" />}
          <div>
            <div className="text-sm font-bold text-slate-800">{o?.nome || grp.fornecedor?.nome || "Sem origem"} {o?.codigo && <span className="font-mono text-xs text-slate-400">({o.codigo})</span>}</div>
            <div className="text-[11px] text-slate-500 flex items-center gap-2">
              {grp.fornecedor?.nome && <span>{grp.fornecedor.nome}</span>}
              {o?.localidade && <span className="flex items-center gap-0.5"><MapPin size={10} />{o.localidade}</span>}
              {o?.prazo_habitual && <span className="flex items-center gap-0.5"><Clock size={10} />{o.prazo_habitual}</span>}
            </div>
          </div>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => markAll(grp, "recebido")} data-testid={`log-receber-tudo-${o?.id || "sem"}`}>Marcar tudo recebido</Button>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-white text-slate-400 uppercase text-[10px]">
          <tr><th className="text-left px-3 py-1.5 font-semibold">Pedido</th><th className="text-left px-3 py-1.5 font-semibold">Descrição</th><th className="text-left px-3 py-1.5 font-semibold">Ref.</th><th className="text-center px-3 py-1.5 font-semibold">Qtd</th><th className="text-left px-3 py-1.5 font-semibold">Cliente</th><th className="text-left px-3 py-1.5 font-semibold">Estado</th><th></th></tr>
        </thead>
        <tbody>
          {grp.itens.map((it) => <ItemRow key={it.linha_id} it={it} markState={markState} navigate={navigate} />)}
        </tbody>
      </table>
    </div>
  );
}

export default function Logistica() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/logistica").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const markState = async (it, estado) => {
    try {
      await api.post(`/atendimentos/${it.atendimento_id}/linhas/${it.linha_id}/estado`, { estado });
      toast.success(estado === "recebido" ? "Peça rececionada." : "Marcada em trânsito.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const markAll = async (grp, estado) => {
    try {
      for (const it of grp.itens) {
        await api.post(`/atendimentos/${it.atendimento_id}/linhas/${it.linha_id}/estado`, { estado });
      }
      toast.success("Grupo rececionado.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const recolha = data?.recolha || [];
  const entrega = data?.entrega || [];

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-xl font-bold text-slate-900 mb-1 flex items-center gap-2"><PackageCheck size={20} /> Receção & Logística</h1>
      <p className="text-sm text-slate-500 mb-5">Peças encomendadas por rececionar. A receção pode ser parcial (linha a linha).</p>

      {recolha.length > 0 && (
        <div className="mb-5 rounded-lg border border-orange-300 bg-orange-50 p-3" data-testid="recolha-alerta">
          <div className="flex items-center gap-2 text-orange-800 font-semibold text-sm mb-1"><Hand size={15} /> Recolhas a fazer</div>
          <ul className="text-sm text-orange-900 space-y-0.5">
            {recolha.map((g, i) => (
              <li key={i}>• <b>{g.itens.length}</b> linha(s) para recolher em <b>{(g.origem || {}).nome || "origem"}</b>{(g.origem || {}).localidade ? ` (${g.origem.localidade})` : ""}</li>
            ))}
          </ul>
        </div>
      )}

      <section className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Hand size={16} className="text-orange-600" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-orange-700">A recolher por nós</h2>
          <span className="text-xs text-slate-400">(concessionários / armazéns da nossa zona)</span>
        </div>
        <div className="space-y-3">
          {recolha.map((grp, i) => <GroupCard key={i} grp={grp} recolha markState={markState} markAll={markAll} navigate={navigate} />)}
          {!recolha.length && <div className="text-sm text-slate-400 bg-white rounded-lg border border-dashed border-slate-300 py-6 text-center">Nada para recolher de momento.</div>}
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-2">
          <Truck size={16} className="text-slate-500" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600">A chegar (entrega do fornecedor)</h2>
        </div>
        <div className="space-y-3">
          {entrega.map((grp, i) => <GroupCard key={i} grp={grp} recolha={false} markState={markState} markAll={markAll} navigate={navigate} />)}
          {!entrega.length && <div className="text-sm text-slate-400 bg-white rounded-lg border border-dashed border-slate-300 py-6 text-center">Sem entregas pendentes.</div>}
        </div>
      </section>
    </div>
  );
}
